import { isCurrentVisionPlanOffThatch } from 'components/member_onboarding_v2/utils'
import { Banner } from 'slab'
import { getFormattedDate } from 'utils'
import {
  type MemberOnboardingMemberPlanChangesProps,
  type MemberOnboardingRenewalVisionPlan,
  type MemberOnboardingRestrictions,
} from 'utils/web_api.schemas'

import { PartialCoverageWarningBanner } from '../PartialCoverageWarningBanner/PartialCoverageWarningBanner'
import RenewalsPlanComparisonHeader from '../RenewalsPlanComparisonHeader/RenewalsPlanComparisonHeader'
import RenewalsPlanComparisonTable, {
  type RowValues,
} from '../RenewalsPlanComparisonTable/RenewalsPlanComparisonTable'

export interface VisionPlanChangesTableProps {
  planChangeSummary: MemberOnboardingMemberPlanChangesProps;
  restrictions?: MemberOnboardingRestrictions;
}

export const VisionPlanChangesTable = ({
  planChangeSummary,
  restrictions = {},
}: VisionPlanChangesTableProps) => {
  const currentYear = planChangeSummary.current_plan_year
  const nextYear = planChangeSummary.next_plan_year
  const yearOverYearChange = planChangeSummary.year_over_year_change

  const currentYearVisionPlan = currentYear.vision_plan
  const nextYearVisionPlan = nextYear.vision_plan

  // Early return if no vision plans
  if (!currentYearVisionPlan) {
    return null
  }

  const planCannotBeChanged =
    restrictions?.vision_not_available === 'coverage_ends_mid_year'

  const visionPlanRowValues: RowValues[] = [
    {
      label: 'Monthly premium',
      prevValue: currentYearVisionPlan.premium_amount.formatted_amount,
      newValue:
        nextYearVisionPlan?.premium_amount.formatted_amount || 'Unknown',
      yoyChange: nextYearVisionPlan
        ? yearOverYearChange.vision_amount
        : undefined,
      increaseIsGreen: false,
    },
  ]

  const onThatchVisionPlanRowValues: RowValues[] = [
    {
      label: 'Eye exam copay',
      prevValue: currentYearVisionPlan.eye_exam_copay,
      newValue: nextYearVisionPlan?.eye_exam_copay || 'Unknown',
      yoyChange: nextYearVisionPlan
        ? yearOverYearChange.vision_feature_change.eye_exam_copay
        : undefined,
      increaseIsGreen: false,
    },
    {
      label: 'Frames or contacts allowance',
      prevValue: currentYearVisionPlan.frames_or_contacts_allowance,
      newValue: nextYearVisionPlan?.frames_or_contacts_allowance || 'Unknown',
      // We don't show YOY change for BSCA vision plans because they are going to be in the long text format
      yoyChange:
        nextYearVisionPlan && nextYearVisionPlan?.category === 'bsca_vision'
          ? undefined
          : yearOverYearChange.vision_feature_change
            .frames_or_contacts_allowance,
      increaseIsGreen: false,
    },
  ]

  const offThatchVision = isCurrentVisionPlanOffThatch(planChangeSummary)
  const rows = offThatchVision
    ? visionPlanRowValues
    : [...visionPlanRowValues, ...onThatchVisionPlanRowValues]

  let thereIsNoNextPlanMessage
  if (planCannotBeChanged) {
    thereIsNoNextPlanMessage = 'Your plan will stay the same'
  } else if (
    !nextYearVisionPlan &&
    currentYearVisionPlan?.category !== 'off_thatch_vision'
  ) {
    thereIsNoNextPlanMessage = `Your ${currentYear.plan_year} plan isn't available for ${nextYear.plan_year}.`
  }

  let plansToUseForHeader:
    | MemberOnboardingRenewalVisionPlan
    | null
    | undefined = nextYearVisionPlan
  if (planCannotBeChanged) {
    plansToUseForHeader = currentYearVisionPlan
  } else if (
    !nextYearVisionPlan &&
    currentYearVisionPlan?.category === 'off_thatch_vision'
  ) {
    plansToUseForHeader = null
  }

  return (
    <div className='flex flex-col gap-6 border border-gray-200 rounded-2xl shadow-sm p-4 xs:p-6'>
      <RenewalsPlanComparisonHeader
        plan={plansToUseForHeader}
        planType='vision'
      />
      {offThatchVision && (
        <Banner
          title='You purchased this plan outside of Thatch.'
          type='notice'
        />
      )}
      {planCannotBeChanged && (
        <Banner
          title={`Your current plan ends ${getFormattedDate(
            currentYearVisionPlan?.coverage_end_date
          )}. You won't be able to select a new plan for ${
            nextYear.plan_year
          } coverage and your premium and plans features will remain the same as ${
            currentYear.plan_year
          }. `}
          type='notice'
        />
      )}
      <PartialCoverageWarningBanner
        planChangeSummary={planChangeSummary}
        coverageType='vision'
      />
      <RenewalsPlanComparisonTable
        prevPlanYear={currentYear.plan_year}
        currPlanYear={nextYear.plan_year}
        rows={rows}
        onNavigateToPrevPlanDetailsPage={
          !offThatchVision
            ? () => {
                window.open(currentYearVisionPlan.sbc_url || '', '_blank')
              }
            : undefined
        }
        onNavigateToCurrPlanDetailsPage={
          !planCannotBeChanged && nextYearVisionPlan
            ? () => {
                window.open(nextYearVisionPlan?.sbc_url || '', '_blank')
              }
            : undefined
        }
        thereIsNoNextPlanMessage={thereIsNoNextPlanMessage}
      />
    </div>
  )
}
